import { PaymentType } from '../../components/PaymentGateway/interfaces/payment_types';
import { FullDate } from '../../components/Shops/Tip/interfaces/FullDate';
import { Money, MoneyFromAPI } from '../../utils/money/Money';

export interface FeeTierFromAPI {
  code: string;
  icon_url: string;
  background_icon_color: string;
  label_color: string;
  tipster_fee_percentage: number;
  values: Record<string, FeeTierValueFromAPI>;
  default_value: FeeTierValueFromAPI;
}

interface FeeTierValueFromAPI {
  from: MoneyFromAPI;
  to: MoneyFromAPI;
}

export class FeeTierValue {
  from: Money;
  to: Money;

  constructor(feeTierValueFromAPI: FeeTierValueFromAPI) {
    this.from = new Money(feeTierValueFromAPI.from);
    this.to = new Money(feeTierValueFromAPI.to);
  }
}

interface IFeeTiers {
  getCurrentTier(currency: string): FeeTier;
  getNextTier(currency: string): FeeTier;
  getCurrentColor(currency: string): string;
}

export class FeeTiers implements IFeeTiers {
  tiers: FeeTier[] = [];
  grossIncome: Money;

  constructor(tiers: FeeTier[] = [], grossIncome: Money) {
    this.tiers = tiers;
    this.grossIncome = grossIncome;
  }

  getCurrentTier(currency: string): FeeTier {
    const highestIndex =
      this.tiers[0].values[currency]?.from?.amount < this.grossIncome?.amount;
    if (highestIndex) {
      return this.tiers[0];
    }
    const currentTier = this.tiers.find(
      (tier: FeeTier) =>
        tier.values[currency]?.from?.amount <= this.grossIncome?.amount &&
        tier.values[currency]?.to?.amount >= this.grossIncome?.amount,
    );

    if (!currentTier) {
      return this.tiers[this.tiers.length - 1];
    }

    return currentTier;
  }

  getNextTier(currency: string): FeeTier {
    const highestIndex =
      this.tiers[0].values[currency]?.from?.amount < this.grossIncome?.amount;
    if (highestIndex) {
      return this.tiers[0];
    }

    const index = this.tiers.findIndex(
      (tier: FeeTier) =>
        tier.values[currency]?.from?.amount <= this.grossIncome?.amount &&
        tier.values[currency]?.to?.amount >= this.grossIncome?.amount,
    );

    if (!index || index < 0) {
      return this.tiers[this.tiers.length - 2];
    }

    return this.tiers[index - 1];
  }

  getCurrentColor(currency: string): string {
    const highestIndex =
      this.tiers[0].values[currency]?.from?.amount < this.grossIncome?.amount;
    if (highestIndex) {
      return `#${this.tiers[0].backgroundIconColor}`;
    }

    const currentTier = this.tiers.find(
      (tier: FeeTier) =>
        tier.values[currency]?.from?.amount <= this.grossIncome?.amount &&
        tier.values[currency]?.to?.amount >= this.grossIncome?.amount,
    );

    if (!currentTier) {
      return `#${this.tiers[this.tiers.length - 1].backgroundIconColor}`;
    }

    return `#${currentTier?.backgroundIconColor}`;
  }
}

interface IFeeTier {
  formatFeeRange(countryCode: string): string;
}

export class FeeTier implements IFeeTier {
  public code: string;
  public iconURL: string;
  public backgroundIconColor: string;
  public labelColor: string;
  public tipsterFeePercentage: number;
  public values: Record<string, FeeTierValue>;

  constructor(feeTierFromAPI: FeeTierFromAPI) {
    this.code = feeTierFromAPI.code;
    this.iconURL = feeTierFromAPI.icon_url;
    this.backgroundIconColor = feeTierFromAPI.background_icon_color;
    this.labelColor = feeTierFromAPI.label_color;
    this.tipsterFeePercentage = feeTierFromAPI.tipster_fee_percentage;
    this.values = this.buildValues(
      feeTierFromAPI.values,
      feeTierFromAPI.default_value,
    );
  }

  private buildValues(
    valuesFromAPI: Record<string, FeeTierValueFromAPI>,
    defaultValue: FeeTierValueFromAPI,
  ): Record<string, FeeTierValue> {
    const newValues: Record<string, FeeTierValue> = {};

    for (let countryCode in valuesFromAPI) {
      newValues[countryCode] = new FeeTierValue(valuesFromAPI[countryCode]);
    }

    newValues['USD'] = new FeeTierValue(defaultValue);

    return newValues as Record<string, FeeTierValue>;
  }

  public formatFeeRange(countryCode: string): string {
    const fromRange =
      this.values[countryCode].from.amount /
      Math.pow(10, this.values[countryCode].from.decimals);

    if (this.values[countryCode].to.amount === 0) {
      return `>${fromRange.toLocaleString()} ${this.values[
        countryCode
      ].from.currency.badge()}`;
    }

    return `${fromRange.toLocaleString()}-${this.values[
      countryCode
    ].to.toString()}`;
  }
}

export interface SalesDataFromAPI {
  sales: SaleFromAPI[];
  summary: SalesSummaryFromAPI;
}

export type SaleTransactionType =
  | 'TIP_SALE'
  | 'TIP_SALE_REFUND'
  | 'PREMIUM_SALE'
  | 'PREMIUM_SALE_REFUND'
  | 'PREMIUM_FRAUD'
  | 'HISTORIC_TIP_SALE'
  | 'PRODUCT_SALE'
  | 'PRODUCT_SALE_REFUND'
  | 'TRANSFER_RECEIVED'
  | 'INCOME'
  | 'REFERRAL_RECEIVED'
  | 'TIP_COUPON_SALE'
  | 'TIER_PREMIUM_SALE'
  | 'TIER_PREMIUM_SALE_REFUND';

export interface SaleFromAPI {
  country_code: string;
  created_at: string;
  id: string;
  gross_income: MoneyFromAPI;
  store: PaymentType;
  tipster_id: string;
  type: SaleTransactionType;
  metadata: any[];
}

export class Sale {
  countryCode: string;
  createdAt: FullDate;
  ID: string;
  grossIncome: Money;
  store: PaymentType;
  tipsterID: string;
  type: SaleTransactionType;
  metadata: any[];

  constructor(saleFromAPI: SaleFromAPI) {
    this.countryCode = saleFromAPI.country_code;
    this.createdAt = new FullDate(saleFromAPI.created_at);
    this.ID = saleFromAPI.id;
    this.grossIncome = new Money(saleFromAPI.gross_income);
    this.store = saleFromAPI.store;
    this.tipsterID = saleFromAPI.tipster_id;
    this.type = saleFromAPI.type;
    this.metadata = saleFromAPI.metadata;
  }
}

export interface SalesSummaryFromAPI {
  premiums_count: number;
  tips_count: number;
  premiums_sales: MoneyFromAPI;
  tips_sales: MoneyFromAPI;
  total_sales: MoneyFromAPI;
  graph: GraphFromAPI;
  current_month_gross_revenue: MoneyFromAPI;
}

export class SalesSummary {
  premiumsCount: number;
  tipsCount: number;
  premiumsSales: Money;
  tipsSales: Money;
  totalSales: Money;
  graph: GraphFromAPI;
  currentMonthGrossRevenue: Money;

  constructor(salesSummaryFromAPI: SalesSummaryFromAPI) {
    this.premiumsCount = salesSummaryFromAPI.premiums_count;
    this.tipsCount = salesSummaryFromAPI.tips_count;
    this.premiumsSales = new Money(salesSummaryFromAPI.premiums_sales);
    this.tipsSales = new Money(salesSummaryFromAPI.tips_sales);
    this.totalSales = new Money(salesSummaryFromAPI.total_sales);
    this.currentMonthGrossRevenue = new Money(
      salesSummaryFromAPI.current_month_gross_revenue,
    );
    this.graph = salesSummaryFromAPI.graph;
  }
}

export interface GraphFromAPI {
  columns: GraphColumnFromAPI[];
}

export interface GraphColumnFromAPI {
  name: string;
  premium_sales: MoneyFromAPI;
  tips_sales: MoneyFromAPI;
}

export class SalesData {
  public summary: SalesSummary;
  public sales: Sale[];

  constructor(salesDataFromAPI: SalesDataFromAPI) {
    this.summary = new SalesSummary(salesDataFromAPI.summary);
    this.sales = salesDataFromAPI.sales.map(
      (saleFromAPI: SaleFromAPI) => new Sale(saleFromAPI),
    );
  }
}

export type BalancePayoutType = 'BANK' | 'PAYPAL';

interface BalanceSummaryFromAPI {
  available_balance: MoneyFromAPI;
  payout_method: BalancePayoutMethodFromAPI;
}

export interface BalancePayoutMethodFromAPI {
  bank_address: string;
  bank_name: string;
  clabe: string;
  swift_code: string;
  type: BalancePayoutType;
  value: string;
}

export interface BalancePayoutMethod {
  bankAddress: string;
  bankName: string;
  clabe: string;
  swiftCode: string;
  type: BalancePayoutType;
  value: string;
}

export class BalanceSummary {
  availableBalance: Money;
  payoutMethod: BalancePayoutMethod;

  constructor(balanceSummaryFromAPI: BalanceSummaryFromAPI) {
    this.availableBalance = new Money(balanceSummaryFromAPI.available_balance);
    this.payoutMethod = {
      bankAddress: balanceSummaryFromAPI.payout_method.bank_address,
      bankName: balanceSummaryFromAPI.payout_method.bank_name,
      clabe: balanceSummaryFromAPI.payout_method.clabe,
      swiftCode: balanceSummaryFromAPI.payout_method.swift_code,
      type: balanceSummaryFromAPI.payout_method.type,
      value: balanceSummaryFromAPI.payout_method.value,
    };
  }
}

export class Balance {
  ID: string;
  countryCode: string;
  createdAt: FullDate;
  metadata: { concept: string };
  netIncome: Money;
  store: string;
  tipsterID: string;
  type: string;

  constructor(balanceFromAPI: BalanceFromAPI) {
    this.ID = balanceFromAPI.id;
    this.countryCode = balanceFromAPI.country_code;
    this.createdAt = new FullDate(balanceFromAPI.created_at);
    this.metadata = balanceFromAPI.metadata;
    this.netIncome = new Money(balanceFromAPI.net_income);
    this.store = balanceFromAPI.store;
    this.tipsterID = balanceFromAPI.tipster_id;
    this.type = balanceFromAPI.type;
  }
}

export interface BalanceFromAPI {
  country_code: string;
  created_at: string;
  id: string;
  metadata: {
    concept: string;
  };
  net_income: MoneyFromAPI;
  store: string;
  tipster_id: string;
  type: string;
}

export interface BalanceDataFromAPI {
  summary: BalanceSummaryFromAPI;
  transactions: BalanceFromAPI[];
}

export class BalanceData {
  public summary: BalanceSummary;
  public balances: Balance[];

  constructor(balanceDataFromAPI: BalanceDataFromAPI) {
    this.summary = new BalanceSummary(balanceDataFromAPI.summary);
    this.balances = balanceDataFromAPI.transactions.map(
      (transaction: BalanceFromAPI) => new Balance(transaction),
    );
  }
}

interface ITipsterFeeTier {}

export class TipsterFeeTier implements ITipsterFeeTier {}
